/**
 * callAudio.ts — records the full call (both voices) to a WAV Blob.
 *
 * The customer is captured from the microphone; the agent's voice is captured by
 * feeding server-side TTS audio (espeak-ng WAV) into the same mix bus. This makes
 * the recording contain both voices regardless of speakers/headphones.
 */
export class CallAudio {
  private ctx: AudioContext | null = null;
  private mixBus: GainNode | null = null;
  private processor: ScriptProcessorNode | null = null;
  private silent: GainNode | null = null;
  private micSource: MediaStreamAudioSourceNode | null = null;
  private chunks: Float32Array[] = [];
  private sampleRate = 48000;
  private recording = false;

  ensureCtx(): AudioContext | null {
    if (!this.ctx) {
      const AC = (window as any).AudioContext || (window as any).webkitAudioContext;
      if (!AC) return null;
      const ctx: AudioContext = new AC();
      this.ctx = ctx;
      this.sampleRate = ctx.sampleRate;
    }
    return this.ctx;
  }

  async startRecording(micStream: MediaStream): Promise<void> {
    const ctx = this.ensureCtx();
    if (!ctx) return;
    const mix = ctx.createGain();
    this.mixBus = mix;
    this.micSource = ctx.createMediaStreamSource(micStream);
    this.micSource.connect(mix); // customer voice into the mix (not to speakers)
    const proc = ctx.createScriptProcessor(4096, 1, 1);
    this.processor = proc;
    this.chunks = [];
    this.recording = true;
    proc.onaudioprocess = (e: AudioProcessingEvent) => {
      if (this.recording) this.chunks.push(new Float32Array(e.inputBuffer.getChannelData(0)));
    };
    const silent = ctx.createGain();
    silent.gain.value = 0;
    this.silent = silent;
    mix.connect(proc);
    proc.connect(silent);
    silent.connect(ctx.destination);
    if (ctx.state === 'suspended') { try { await ctx.resume(); } catch {} }
  }

  isRecording(): boolean { return this.recording; }

  /** Play agent TTS audio aloud (and capture it into the recording if active).
   * Resolves when playback finishes. */
  async play(data: ArrayBuffer): Promise<void> {
    const ctx = this.ensureCtx();
    if (!ctx) return;
    if (ctx.state === 'suspended') { try { await ctx.resume(); } catch {} }
    let buf: AudioBuffer;
    try { buf = await ctx.decodeAudioData(data.slice(0)); } catch { return; }
    const src = ctx.createBufferSource();
    src.buffer = buf;
    src.connect(ctx.destination);                       // heard live
    if (this.recording && this.mixBus) src.connect(this.mixBus); // recorded
    await new Promise<void>((res) => { src.onended = () => res(); try { src.start(); } catch { res(); } });
  }

  stop(): Blob | null {
    this.recording = false;
    const sr = this.sampleRate;
    try { this.processor?.disconnect(); } catch {}
    try { this.micSource?.disconnect(); } catch {}
    try { this.silent?.disconnect(); } catch {}
    try { this.mixBus?.disconnect(); } catch {}
    try { this.ctx?.close(); } catch {}
    this.ctx = null; this.processor = null; this.micSource = null; this.silent = null; this.mixBus = null;
    if (!this.chunks.length) return null;
    const samples = flatten(this.chunks);
    this.chunks = [];
    if (!samples.length) return null;
    return encodeWav(samples, sr);
  }
}

function flatten(chunks: Float32Array[]): Float32Array {
  let len = 0;
  for (const c of chunks) len += c.length;
  const out = new Float32Array(len);
  let off = 0;
  for (const c of chunks) { out.set(c, off); off += c.length; }
  return out;
}

function encodeWav(samples: Float32Array, sampleRate: number): Blob {
  const buffer = new ArrayBuffer(44 + samples.length * 2);
  const view = new DataView(buffer);
  const w = (off: number, s: string) => { for (let i = 0; i < s.length; i++) view.setUint8(off + i, s.charCodeAt(i)); };
  w(0, 'RIFF'); view.setUint32(4, 36 + samples.length * 2, true); w(8, 'WAVE'); w(12, 'fmt ');
  view.setUint32(16, 16, true); view.setUint16(20, 1, true); view.setUint16(22, 1, true);
  view.setUint32(24, sampleRate, true); view.setUint32(28, sampleRate * 2, true);
  view.setUint16(32, 2, true); view.setUint16(34, 16, true); w(36, 'data');
  view.setUint32(40, samples.length * 2, true);
  let off = 44;
  for (let i = 0; i < samples.length; i++, off += 2) {
    const s = Math.max(-1, Math.min(1, samples[i]));
    view.setInt16(off, s < 0 ? s * 0x8000 : s * 0x7fff, true);
  }
  return new Blob([view], { type: 'audio/wav' });
}

export function blobToBase64(blob: Blob): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onloadend = () => {
      const res = String(reader.result || '');
      resolve(res.includes(',') ? res.split(',')[1] : res);
    };
    reader.onerror = reject;
    reader.readAsDataURL(blob);
  });
}
